import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Spin } from 'antd';
import { fetchExperimentCostBreakdown } from '../../data/costRulesApi';

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const GREEN = '#2e7d32';
const RED = '#c62828';
const GREY = '#787774';

const round2 = (x) => Math.round(x * 100) / 100;
const eur = (value) => `${value < 0 ? '-' : ''}€${Math.abs(Number(value) || 0).toFixed(2)}`;
const signedEur = (value) => `${value > 0 ? '+' : ''}${eur(value)}`;
const num = (value) => (Number.isInteger(round2(value)) ? String(round2(value)) : value.toFixed(1));
const signedNum = (value) => `${value > 0 ? '+' : ''}${num(value)}`;
const pct = (value) => (value === null ? '-' : `${value.toFixed(1)} %`);

// Supplier cost first, then IVA, other taxes, the canon, and what the current rules no longer explain last.
const RANK = { base: 0, iva: 1, canon: 3, sin_desglosar: 4 };
const rank = (component) => RANK[component] ?? 2;
const costKey = (cost) => `${cost.component}|${cost.tag ?? ''}`;

const BASELINE_LABEL = { historical: 'historical', year_ago: 'yearAgo', '4weeks': 'costBaseline4weeks' };

/** What the grid shows for one side: revenue, minus taxes = net revenue, minus supplier cost = profit. */
const figures = (side) => {
  const amountOf = (key) => side.costs.filter((cost) => costKey(cost) === key).reduce((sum, cost) => sum + cost.amount, 0);
  const taxes = side.costs.filter((cost) => cost.component !== 'base').reduce((sum, cost) => sum + cost.amount, 0);
  const netRevenue = round2(side.revenue - taxes);
  const profit = round2(side.revenue - side.totalCost);
  return {
    amountOf,
    units: side.units,
    avgPrice: side.units > 0 ? side.revenue / side.units : null,
    revenue: side.revenue,
    netRevenue,
    base: round2(amountOf('base|')),
    profit,
    marginPct: netRevenue > 0 ? (profit / netRevenue) * 100 : null,
  };
};

/**
 * Wraps a gross profit value — one experiment, or a row grouping several (a machine's slots, a group,
 * route or sector) — and a click opens it next to its baseline: units, price, revenue, each tax by the
 * product tag that charged it, net revenue, supplier cost, gross profit and margin, with the
 * difference and a one-line verdict. The backend adds the experiments up.
 */
const CostBreakdownPopover = ({ experimentIds, customerId, baseline, children }) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState({ status: 'idle', data: null });
  const idsKey = experimentIds.join(',');

  const load = React.useCallback(async () => {
    setState({ status: 'loading', data: null });
    try {
      const data = await fetchExperimentCostBreakdown(customerId, idsKey.split(',').map(Number), baseline);
      setState({ status: 'ready', data });
    } catch (error) {
      setState({ status: error?.response?.status === 404 ? 'notMeasured' : 'error', data: null });
    }
  }, [customerId, idsKey, baseline]);

  const costLabel = (cost) => cost.tag || t(`costComponent_${cost.component}`, { defaultValue: cost.component });

  const renderContent = () => {
    if (state.status === 'loading' || state.status === 'idle') {
      return <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>;
    }
    if (state.status === 'notMeasured') return <span>{t('costBreakdownNotMeasured')}</span>;
    if (state.status === 'error') return <span>{t('costBreakdownError')}</span>;

    const { before, after } = state.data;
    const hasBefore = before.hasData !== false;
    const b = figures(before);
    const a = figures(after);

    const taxes = new Map();
    [...before.costs, ...after.costs].filter((cost) => cost.component !== 'base').forEach((cost) => taxes.set(costKey(cost), cost));
    // IVA and the client's canon always have their rows, so products without IVA tags or a machine without a
    // canon read "-" (nothing deducted) rather than the step going missing.
    for (const component of ['iva', 'canon']) {
      if (![...taxes.values()].some((cost) => cost.component === component)) {
        taxes.set(`${component}|`, { component, tag: null, amount: 0 });
      }
    }
    const taxRows = [...taxes.values()].sort((x, y) => rank(x.component) - rank(y.component)
      || x.component.localeCompare(y.component) || (x.tag || '').localeCompare(y.tag || ''));

    const deltaUnits = a.units - b.units;
    const deltaProfit = round2(a.profit - b.profit);
    const deltaMargin = a.marginPct !== null && b.marginPct !== null ? a.marginPct - b.marginPct : null;
    const tone = (delta) => (delta > 0 ? GREEN : delta < 0 ? RED : GREY);
    // The three differences that tell the story (units, gross profit, margin) are shown as bubbles.
    const bubble = (text, delta) => ({ text, color: tone(delta) });

    // label, before, during, difference, emphasis
    const rows = [
      [t('experimentsUnitsHeader'), num(b.units), num(a.units), bubble(signedNum(deltaUnits), deltaUnits)],
      [t('averagePrice'), b.avgPrice === null ? '-' : eur(b.avgPrice), a.avgPrice === null ? '-' : eur(a.avgPrice),
        b.avgPrice === null || a.avgPrice === null ? '-' : signedEur(a.avgPrice - b.avgPrice)],
      [t('costRevenueGross'), eur(b.revenue), eur(a.revenue), signedEur(a.revenue - b.revenue)],
      ...taxRows.map((cost) => {
        const key = costKey(cost);
        const before$ = b.amountOf(key);
        const after$ = a.amountOf(key);
        return [`− ${costLabel(cost)}`, before$ ? eur(before$) : '-', after$ ? eur(after$) : '-',
          before$ || after$ ? signedEur(after$ - before$) : '-'];
      }),
      [`= ${t('costNetRevenue')}`, eur(b.netRevenue), eur(a.netRevenue), signedEur(a.netRevenue - b.netRevenue), 'sub'],
      [`− ${t('costComponent_base')}`, eur(b.base), eur(a.base), signedEur(a.base - b.base)],
      [`= ${t('grossProfit')}`, eur(b.profit), eur(a.profit), bubble(signedEur(deltaProfit), deltaProfit), 'total'],
      [t('costMarginPct'), pct(b.marginPct), pct(a.marginPct), deltaMargin === null ? '-'
        : bubble(`${deltaMargin > 0 ? '+' : ''}${deltaMargin.toFixed(1)} ${t('costPointsUnit')}`, Math.abs(deltaMargin) < 0.05 ? 0 : deltaMargin)],
    ];

    // One experiment names its columns by plan (the product it replaces → the one it puts in, even before
    // that one sells); a row adding several up just says it sums them all.
    const single = !(state.data.experiments > 1);
    const sold = (side) => side.products.filter((p) => p.units > 0).map((p) => p.productName || p.dProductId).join(' + ') || '-';
    const columnLabel = (side, plannedName) => (single ? plannedName || sold(side) : t('costSumAllProducts'));
    const planned = state.data.plannedProducts;
    const unmatched = state.data.unmatched;
    const unmatchedProfit = unmatched ? round2(unmatched.revenue - unmatched.totalCost) : 0;
    const cell = { padding: '3px 10px', textAlign: 'right', whiteSpace: 'nowrap' };
    const head = { ...cell, fontWeight: 600, verticalAlign: 'bottom' };
    const renderDiff = (diff) => (typeof diff === 'object'
      ? (
        <span data-tone={diff.color} style={{
          color: diff.color, border: `1px solid ${diff.color}`, borderRadius: 10, padding: '1px 8px', fontWeight: 600,
        }}>
          {diff.text}
        </span>
      )
      : diff);

    return (
      <div style={{ fontFamily: FONT, fontSize: 12.5, color: '#37352f', maxWidth: 640 }}>
        {!hasBefore && <div style={{ color: GREY, marginBottom: 10 }}>{t('costNoBaseline')}</div>}

        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E5E5' }}>
              <th style={{ ...head, textAlign: 'left' }} />
              <th style={head}>
                {t(BASELINE_LABEL[state.data.baseline] || 'historical')}
                <div style={{ fontWeight: 400, color: GREY, maxWidth: 160, whiteSpace: 'normal' }}>{columnLabel(before, planned?.before)}</div>
              </th>
              <th style={head}>
                {t('costDuringExperiment')}
                <div style={{ fontWeight: 400, color: GREY, maxWidth: 160, whiteSpace: 'normal' }}>{columnLabel(after, planned?.after)}</div>
              </th>
              <th style={head}>{t('costDifference')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, beforeValue, afterValue, diff, emphasis]) => (
              <tr key={label} style={{
                fontWeight: emphasis === 'total' ? 600 : 400,
                borderTop: emphasis ? '1px solid #E5E5E5' : 'none',
              }}>
                <td style={{ ...cell, textAlign: 'left', whiteSpace: 'normal', maxWidth: 220 }}>{label}</td>
                <td style={cell}>{hasBefore ? beforeValue : '-'}</td>
                <td style={cell}>{afterValue}</td>
                <td style={{ ...cell, color: GREY }}>{hasBefore ? renderDiff(diff) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ color: GREY, fontSize: 11, marginTop: 8 }}>
          {state.data.experiments > 1 && <div>{t('costExperimentsCount', { n: state.data.experiments })}</div>}
          {unmatched?.count > 0 && (
            <div>{t('costUnmatched', { n: unmatched.count, value: signedEur(unmatchedProfit) })}</div>
          )}
          {state.data.sameDays && (
            <div>{state.data.sameDays.days != null ? t('costSameDays', { count: state.data.sameDays.days }) : t('costSameDaysMixed')}</div>
          )}
          {t('costBreakdownVatIncluded')}
          {(!before.hasSplit || !after.hasSplit) && <div>{t('costLinesPending')}</div>}
        </div>
      </div>
    );
  };

  // The popover renders in a portal, but React still bubbles its clicks to the table row, whose own
  // click expands or collapses the row: keep them inside.
  const stop = (e) => e.stopPropagation();

  return (
    <Popover
      trigger="click"
      placement="leftTop"
      title={<div onClick={stop}>{t('costBreakdownTitle')}</div>}
      content={<div onClick={stop}>{renderContent()}</div>}
      onOpenChange={(open) => { if (open) load(); }}
    >
      <button
        type="button"
        aria-label={t('costBreakdownOpen')}
        title={t('costBreakdownOpen')}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          textDecoration: 'underline dotted', textUnderlineOffset: 3, font: 'inherit',
        }}
      >
        {children}
      </button>
    </Popover>
  );
};

export default CostBreakdownPopover;
